import type { Locale } from "@adpilot/shared";
import { useMemo } from "react";
import { useLocaleStore } from "@/stores/locale";

/**
 * 统一的 locale-aware 格式化入口（ADP-74）。
 * 规则：金额币种由 marketplace 决定，locale 只影响展示格式（千分位、货币符号位置等）。
 * 组件内优先使用 useFormatters()（订阅语言切换自动重渲染）；
 * 非组件上下文可直接调用导出的函数，默认取当前语言。
 */

const numberFormatCache = new Map<string, Intl.NumberFormat>();
const dateFormatCache = new Map<string, Intl.DateTimeFormat>();
export const DEFAULT_TIME_ZONE = "Asia/Shanghai";

function numberFormatter(locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    numberFormatCache.set(key, formatter);
  }
  return formatter;
}

function dateFormatter(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = dateFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatCache.set(key, formatter);
  }
  return formatter;
}

function currentLocale(): Locale {
  return useLocaleStore.getState().locale;
}

/** 金额：currency 来自 marketplace（如 "USD"），不随 locale 改变 */
export function formatCurrency(
  value: number,
  currency = "USD",
  locale: Locale = currentLocale(),
): string {
  return numberFormatter(locale, {
    style: "currency",
    currency,
    currencyDisplay: currency === "USD" ? "narrowSymbol" : "symbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumber(
  value: number,
  locale: Locale = currentLocale(),
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 },
): string {
  return numberFormatter(locale, options).format(value);
}

/** 比率转百分比：0.253 → "25.3%" */
export function formatPercent(
  ratio: number,
  fractionDigits = 1,
  locale: Locale = currentLocale(),
): string {
  return numberFormatter(locale, {
    style: "percent",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(ratio);
}

/** 百分点数值转百分比：25.3 → "25.3%"（用于后端已按百分点返回的字段，如 ACOS） */
export function formatPercentPoints(
  points: number,
  fractionDigits = 1,
  locale: Locale = currentLocale(),
): string {
  return formatPercent(points / 100, fractionDigits, locale);
}

type DateInput = Date | string | number;

function toDate(input: DateInput): Date {
  return input instanceof Date ? input : new Date(input);
}

/** 日期：en-US "Jul 27, 2026" / zh-CN "2026年7月27日" */
export function formatDate(input: DateInput, locale: Locale = currentLocale()): string {
  return dateFormatter(locale, { dateStyle: "medium", timeZone: DEFAULT_TIME_ZONE }).format(
    toDate(input),
  );
}

/** 日期时间：en-US "Jul 27, 2026, 3:04 PM" / zh-CN "2026年7月27日 15:04" */
export function formatDateTime(input: DateInput, locale: Locale = currentLocale()): string {
  return dateFormatter(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: DEFAULT_TIME_ZONE,
  }).format(toDate(input));
}

/** 订阅语言切换的格式化 Hook — 业务组件的默认选择 */
export function useFormatters() {
  const locale = useLocaleStore((s) => s.locale);
  return useMemo(
    () => ({
      locale,
      formatCurrency: (value: number, currency = "USD") => formatCurrency(value, currency, locale),
      formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
        formatNumber(value, locale, options),
      formatPercent: (ratio: number, fractionDigits = 1) =>
        formatPercent(ratio, fractionDigits, locale),
      formatPercentPoints: (points: number, fractionDigits = 1) =>
        formatPercentPoints(points, fractionDigits, locale),
      formatDate: (input: DateInput) => formatDate(input, locale),
      formatDateTime: (input: DateInput) => formatDateTime(input, locale),
    }),
    [locale],
  );
}

/**
 * 建议竞价相对当前竞价的带符号绝对差值(ADP-127),如 "+$0.20" / "−$0.15"。
 * 币种跟随传入的 formatCurrency(由 marketplace 决定);不展示百分比。
 */
export function formatSignedCurrencyDiff(
  oldBid: number,
  newBid: number,
  currencyFormatter: (value: number) => string,
): string {
  const diff = newBid - oldBid;
  const formatted = currencyFormatter(Math.abs(diff));
  if (diff === 0) return formatted;
  return `${diff > 0 ? "+" : "−"}${formatted}`;
}
